import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '@/stores/useAppStore';
import { useGroupStore } from '@/stores/useGroupStore';
import { gamificationService } from '@/services/gamificationService';
import { toast } from 'sonner';

/**
 * Hook para sincronizar atividades de gamificação com grupos
 * ✅ Bug 5 Fix: Corrigido detecção de mudanças e closures stale
 */
export const useGamificationSync = () => {
  // ✅ Usar getState() para evitar closures stale
  const previousBadges = useRef<number>(0);
  const previousStreak = useRef<number>(0);
  const previousCompletedGoalIds = useRef<Set<string>>(new Set());
  const previousCheckinCount = useRef<number>(0);
  const isProcessing = useRef<boolean>(false);

  // Obter estado fresco a cada verificação
  const getState = useCallback(() => {
    const { userBadges, challenges, goals } = useAppStore.getState();
    const { myGroups } = useGroupStore.getState();
    const challenge = challenges.find(c => !c.archived) || null;
    return { userBadges, challenge, goals, myGroups };
  }, []);

  // Detectar novo badge
  useEffect(() => {
    const { userBadges, myGroups } = getState();
    
    if (userBadges.length > previousBadges.current && myGroups.length > 0) {
      const newBadge = userBadges[0]; // Mais recente
      
      console.log('🏆 [GamificationSync] Novo badge detectado:', newBadge.badgeId);
      
      myGroups.forEach(group => {
        gamificationService.createActivityWithPoints({
          groupId: group.id,
          tipo: 'badge',
          payload: {
            badge_id: newBadge.badgeId,
            badge_name: newBadge.badgeId,
            earned_at: newBadge.earnedAt
          },
          eventType: 'badge_earned'
        }).catch(err => console.error('Erro ao criar atividade de badge:', err));
      });
    }
    previousBadges.current = userBadges.length;
  }, [useAppStore((s) => s.userBadges.length), getState]);

  // Detectar marcos de streak
  useEffect(() => {
    const { challenge, myGroups } = getState();
    const currentStreak = challenge?.streak || 0;
    const streakMilestones = [7, 21, 66, 90];
    
    const newMilestone = streakMilestones.find(
      milestone => currentStreak >= milestone && previousStreak.current < milestone
    );
    
    if (newMilestone && myGroups.length > 0) {
      console.log('🔥 [GamificationSync] Marco de streak atingido:', newMilestone);
      
      myGroups.forEach(group => {
        gamificationService.createActivityWithPoints({
          groupId: group.id,
          tipo: 'streak',
          payload: {
            streak: currentStreak,
            milestone: newMilestone
          },
          eventType: `streak_${newMilestone}`
        }).catch(err => console.error('Erro ao criar atividade de streak:', err));
      });
    }
    
    previousStreak.current = currentStreak;
  }, [useAppStore((s) => s.challenges.find(c => !c.archived)?.streak), getState]);

  // Detectar meta completada - ✅ CORRIGIDO: Comparação por IDs
  useEffect(() => {
    const { goals, myGroups } = getState();
    
    const completedGoalIds = new Set(
      goals.filter(g => g.status === 'concluido').map(g => g.id)
    );
    
    // Encontrar IDs que são novos (não estavam no set anterior)
    const newlyCompletedIds = [...completedGoalIds].filter(
      id => !previousCompletedGoalIds.current.has(id)
    );
    
    if (newlyCompletedIds.length > 0 && myGroups.length > 0) {
      newlyCompletedIds.forEach(goalId => {
        const goal = goals.find(g => g.id === goalId);
        if (!goal) return;
        
        console.log('🎯 [GamificationSync] Meta concluída:', goal.title);
        
        myGroups.forEach(group => {
          gamificationService.createActivityWithPoints({
            groupId: group.id,
            tipo: 'meta_concluida',
            payload: {
              goal_title: goal.title,
              pillar: goal.pillar
            },
            eventType: 'goal_completed'
          }).catch(err => console.error('Erro ao criar atividade de meta:', err));
        });
      });
    }
    
    previousCompletedGoalIds.current = completedGoalIds;
  }, [useAppStore((s) => s.goals.filter(g => g.status === 'concluido').length), getState]);

  // Detectar check-in diário
  useEffect(() => {
    const { challenge, myGroups } = getState();
    const currentCheckins = challenge?.completedDates?.length || 0;
    
    if (currentCheckins > previousCheckinCount.current && myGroups.length > 0) {
      const latestDate = challenge?.completedDates?.[currentCheckins - 1];
      
      if (latestDate) {
        console.log('✅ [GamificationSync] Check-in detectado:', latestDate);
        
        myGroups.forEach(group => {
          gamificationService.createActivityWithPoints({
            groupId: group.id,
            tipo: 'checkin',
            payload: {
              date: latestDate,
              day_number: currentCheckins,
              total_days: 90
            },
            eventType: 'daily_checkin'
          }).catch(err => console.error('Erro ao criar atividade de checkin:', err));
        });
      }
    }
    
    previousCheckinCount.current = currentCheckins;
  }, [useAppStore((s) => s.challenges.find(c => !c.archived)?.completedDates?.length), getState]);
};
